import { groupBy, sortBy } from "lodash";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";

import { exists, filterMatches, parseObjectiveScope } from "@examspark/common";
import {
  NoResults,
  useQueriesGetObjectives,
  useQueryGetObjective,
  useStabilizedQueries,
} from "@examspark/common-app";
import {
  CheckboxInput,
  HSpace,
  Label,
  SearchField,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import { LabelText } from "@examspark/common-ui/dist/components/inputs/Label";
import {
  JumpBar,
  JumpBarContext,
  useJumpBar,
} from "@examspark/common-ui/dist/components/utils/JumpBar";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import {
  getObjectiveCategory,
  getObjectiveCurriculumUsage,
  ObjectiveCategoryBadge,
  ObjectiveCurriculumUsageBadge,
} from "@examspark/customer/tcmpc";
import { ParsedUnitObjective, Unit } from "@examspark/types";

import ObjectiveBadge from "../common/ObjectiveBadge";
import ObjectiveDetailModal from "../ObjectiveDetailModal";

import ObjectiveSpecificity from "./ObjectiveSpecificity";
import UnitContent from "./UnitContent";

const Styled = styled.table`
  border-collapse: collapse;

  & tbody tr {
    border-top: 1px solid #ddd;
  }
`;

export const StyledCheckboxLabel = styled(Label)`
  & > input[type="checkbox"] {
    flex-grow: 0;
    order: -1;
  }

  & > ${LabelText} {
    text-transform: initial;
  }
`;

const StyledTableHead = styled.thead`
  & > tr > th:nth-child(1) {
    width: 30%;
  }
  & > tr > th:nth-child(2) {
    width: 70%;
  }

  th {
    text-align: left;
    padding: 1rem;
    color: #333;
    font-size: 1.3rem;
  }
  th:first-child {
    padding-left: 0;
  }
`;

const StyledObjectiveRow = styled.tr<{ isParent?: boolean }>`
  td {
    padding-top: 0.8rem;
    padding-bottom: 0.8rem;
    padding-left: 0.8rem;
    vertical-align: top;
  }
  td:first-child {
    padding-right: 1rem;
  }
  td:last-child {
    padding-right: 0;
  }

  ${(p) =>
    p.isParent &&
    css`
      background-color: ${colorFromTheme("surface2")};
    `}
`;

const StyledStandardCell = styled.td`
  gap: 1rem;
  line-height: 1.5em;
  font-size: 1.3rem;
`;

const StyledSpecificityCell = styled.td`
  padding: 1rem;
  font-size: 1.1rem;
  line-height: 1.5em;
`;

const StyledObjectiveType = styled.div`
  color: ${colorFromTheme("primary")};
  gap: 1rem;
  display: flex;
  flex-wrap: wrap;
`;

const StyledSearchField = styled(SearchField)`
  border: none;
  background-color: #f2f2f2;
  padding: 0 0.7rem;
`;

const StyledCheckboxes = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

const StyledHeaderContent = styled(UnitContent)`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.5rem 0;
  text-align: left;
  text-transform: uppercase;
  line-height: 1.5em;
`;

type Props = {
  objectives: ParsedUnitObjective[] | null | undefined;
  unit?: Unit | null;
  toolbar?: ReactNode;
  initialShowSpecificity?: boolean;
  initialShowEnhancedClarification?: boolean;
  allowEnhancedClarification?: boolean;
  groupByScope?: boolean;
  renderObjectiveDetails?: (
    objective: ParsedUnitObjective,
    isInjected: boolean,
  ) => ReactNode;
};

const Objectives = ({ objectives, unit, toolbar, ...props }: Props) => {
  const [jumpBarEntries, setJumpBarEntries] = useState({});

  const [showSpecificity, setShowSpecificity] = useState(
    props.initialShowSpecificity ?? false,
  );
  const [showEnhancedClarification, setShowEnhancedClarification] = useState(
    props.initialShowEnhancedClarification ?? false,
  );
  const [showAnnotationRcfp, setShowAnnotationRcfp] = useState(false);
  const [showAnnotationCcrs, setShowAnnotationCcrs] = useState(false);

  useEffect(
    () => setShowSpecificity(props.initialShowSpecificity ?? false),
    [props.initialShowSpecificity],
  );
  useEffect(
    () =>
      setShowEnhancedClarification(
        props.initialShowEnhancedClarification ?? false,
      ),
    [props.initialShowEnhancedClarification],
  );

  const [filter, setFilter] = useState<string>();

  const [showIndex, setShowIndex] = useState<number>();

  const filteredObjectives = useMemo(() => {
    if (filter == null) return objectives;

    return objectives?.filter((o) =>
      filterMatches(
        filter,
        o.content,
        o.abbreviation,
        o.notes,
        ...(o.tags ?? []).map((t) => t.value),
      ),
    );
  }, [filter, objectives]);

  const missingParentIds = useMemo(() => {
    const objectiveIds = new Set(objectives?.map((o) => o.id) ?? []);
    return new Set(
      filteredObjectives
        ?.filter((o) => o.parentId != null && !objectiveIds.has(o.parentId))
        .map((o) => o.parentId)
        .filter(exists) ?? [],
    );
  }, [filteredObjectives, objectives]);

  const parentObjectiveQueries = useStabilizedQueries(
    useQueriesGetObjectives(
      Array.from(missingParentIds).map((id) => ({
        id,
        sk: "standard",
      })),
    ),
    [missingParentIds],
  );

  const { flattenedObjectives, groupedObjectives } = useMemo((): {
    flattenedObjectives: ParsedUnitObjective[];
    groupedObjectives: Record<string, ParsedUnitObjective[]>;
  } => {
    const flattenedObjectives = sortBy(
      [
        ...parentObjectiveQueries.map((q) => q.data).filter(exists),
        ...(filteredObjectives ?? []),
      ],
      ["scope", "sequence"],
    );

    const groupedObjectives = groupBy(flattenedObjectives, (o) => o.scope);

    return { flattenedObjectives, groupedObjectives };
  }, [filteredObjectives, parentObjectiveQueries]);

  return (
    <JumpBarContext.Provider
      value={{
        entries: jumpBarEntries,
        setEntries: setJumpBarEntries,
      }}
    >
      <div>
        <ObjectiveDetailModal
          objectives={flattenedObjectives}
          showIndex={showIndex}
          onShowIndexChange={setShowIndex}
          unit={unit}
        />
        <VSpace size={"2rem"}>
          <HSpace
            style={{
              alignItems: "end",
              position: "sticky",
              top: 0,
              backgroundColor: "white",
              zIndex: 1,
              paddingBottom: "1rem",
            }}
          >
            {toolbar}
            <Toolbar>
              <StyledSearchField
                placeholder="Filter Standards"
                value={filter}
                onChange={setFilter}
              />
              <StyledCheckboxes>
                <StyledCheckboxLabel label="Show Specificity" layout="row">
                  <CheckboxInput
                    checked={showSpecificity}
                    onChange={({ checked }) => setShowSpecificity(checked)}
                  />
                </StyledCheckboxLabel>
                {props.allowEnhancedClarification && (
                  <StyledCheckboxLabel
                    label="Enhanced Clarification"
                    layout="row"
                  >
                    <CheckboxInput
                      disabled={!showSpecificity}
                      checked={showEnhancedClarification}
                      onChange={() =>
                        setShowEnhancedClarification(!showEnhancedClarification)
                      }
                    />
                  </StyledCheckboxLabel>
                )}
                <StyledCheckboxLabel label="TxRCFP" layout="row">
                  <CheckboxInput
                    disabled={!showSpecificity}
                    checked={showAnnotationRcfp}
                    onChange={({ checked }) => setShowAnnotationRcfp(checked)}
                  />
                </StyledCheckboxLabel>
                <StyledCheckboxLabel label="CCRS" layout="row">
                  <CheckboxInput
                    disabled={!showSpecificity}
                    checked={showAnnotationCcrs}
                    onChange={({ checked }) => setShowAnnotationCcrs(checked)}
                  />
                </StyledCheckboxLabel>
              </StyledCheckboxes>
              {props.groupByScope && <JumpBar />}
            </Toolbar>
          </HSpace>

          <div>
            {filteredObjectives?.length === 0 && (
              <NoResults>No standards match the current filter.</NoResults>
            )}
            {filteredObjectives?.length !== 0 && (
              <Styled>
                <StyledTableHead>
                  <tr>
                    <th>Standard</th>
                    {showSpecificity && <th>Specificity</th>}
                  </tr>
                </StyledTableHead>
                <tbody>
                  {Object.entries(groupedObjectives).map(
                    ([scope, objectivesInGroup]) => (
                      <ObjectiveScopeList
                        key={scope}
                        scope={scope}
                        objectives={objectivesInGroup}
                        missingParentIds={missingParentIds}
                        showSpecificity={showSpecificity}
                        showAnnotationRcfp={showAnnotationRcfp}
                        showAnnotationCcrs={showAnnotationCcrs}
                        showEnhancedClarification={showEnhancedClarification}
                        showScopeHeader={!!props.groupByScope}
                        renderObjectiveDetails={props.renderObjectiveDetails}
                        onObjectiveClick={(objective) => {
                          setShowIndex(
                            flattenedObjectives?.findIndex(
                              (o) => o.id === objective.id,
                            ),
                          );
                        }}
                      />
                    ),
                  )}
                </tbody>
              </Styled>
            )}
          </div>
        </VSpace>
      </div>
    </JumpBarContext.Provider>
  );
};

type ObjectiveScopeListProps = {
  scope: string;
  objectives: ParsedUnitObjective[];
  missingParentIds: Set<string>;
  showSpecificity: boolean;
  showAnnotationRcfp: boolean;
  showAnnotationCcrs: boolean;
  showEnhancedClarification: boolean;
  showScopeHeader: boolean;
  renderObjectiveDetails:
    | undefined
    | ((objective: ParsedUnitObjective, isInjected: boolean) => ReactNode);
  onObjectiveClick: (objective: ParsedUnitObjective) => void;
};

const StyledJumpBarTarget = styled.tr`
  scroll-margin-top: 60px;
`;

const ObjectiveScopeList = ({
  scope,
  objectives,
  missingParentIds,
  showAnnotationCcrs,
  showAnnotationRcfp,
  showEnhancedClarification,
  showSpecificity,
  showScopeHeader,
  onObjectiveClick,
  renderObjectiveDetails,
}: ObjectiveScopeListProps) => {
  const { authority } = parseObjectiveScope(scope);
  const scopeQuery = useQueryGetObjective(
    showScopeHeader ? authority : undefined,
    `section#${scope}`,
  );

  const headerRef = React.useRef<HTMLTableRowElement>(null);

  useJumpBar(
    {
      label: scopeQuery.data?.content ?? "",
      ref: headerRef,
      isSubsection: false,
    },
    !scopeQuery.isFetched,
  );

  return (
    <>
      <StyledJumpBarTarget ref={headerRef} tabIndex={-1} />
      {showScopeHeader && scopeQuery.isFetched && (
        <StyledObjectiveRow>
          <StyledStandardCell colSpan={2}>
            <StyledHeaderContent content={scopeQuery.data?.content} />
          </StyledStandardCell>
        </StyledObjectiveRow>
      )}
      {objectives.map((objective) => {
        const isAddedParent = missingParentIds.has(objective.id);
        return (
          <StyledObjectiveRow key={objective.id} isParent={isAddedParent}>
            <StyledStandardCell
              colSpan={showSpecificity && isAddedParent ? 2 : 1}
            >
              <span>
                <ObjectiveBadge
                  objective={objective}
                  onClick={() => onObjectiveClick?.(objective)}
                />
              </span>
              <UnitContent
                content={
                  objective.objectivesItem?.data?.content ?? objective.content
                }
              />
              <StyledObjectiveType>
                <ObjectiveCategoryBadge
                  value={getObjectiveCategory(objective)}
                  showLabel
                />
                <ObjectiveCurriculumUsageBadge
                  value={getObjectiveCurriculumUsage(objective.objectivesItem)}
                  showLabel
                />
              </StyledObjectiveType>
              {renderObjectiveDetails?.(objective, false)}
            </StyledStandardCell>
            {showSpecificity && !isAddedParent && (
              <StyledSpecificityCell>
                <ObjectiveSpecificity
                  objective={objective}
                  showAnnotationRcfp={showAnnotationRcfp}
                  showAnnotationCcrs={showAnnotationCcrs}
                  showEnhancedClarification={showEnhancedClarification}
                />
              </StyledSpecificityCell>
            )}
          </StyledObjectiveRow>
        );
      })}
    </>
  );
};

export default Objectives;
